import React, { useMemo, useState } from 'react';
import { MenuBar, TabContent, Tabs } from '@brighterion/br-ui';
import { getRulesFormatForView } from 'Utils';
import PageHeader from 'Components/PageHeader';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    getUser,
    shouldShowManageTenants,
    shouldShowMerchants,
    shouldShowTenantGroups,
    shouldShowAdvancedRules
} from 'Selectors';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import TenantGroupsTable from 'Routes/ManageTenants/components/TenantGroupsTable';
import MerchantOverridesTable from 'Routes/Overrides/components/MerchantsOverridesTable/MerchantsOverridesTable';
import useGetRuleColumns, { Args } from 'Hooks/useGetRuleColumns';
import useRuleView from 'Hooks/useRuleView';
import useGetMerchantOverridesColumns from 'Hooks/useGetMerchantOverridesColumns';
import useGetScheduledDeploymentDetails from 'Hooks/query-hooks/scheduled-deployment/useGetScheduledDeploymentDetails';
import { GLOBAL_RULE_TABLE_VIEW } from 'Common/enums';
import useGetTenantGroupColumns from 'Hooks/useGetTenantGroupColumns';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import GlobalRulesTable from 'Components/GlobalRulesTable';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetScheduledDeployment from 'Hooks/query-hooks/scheduled-deployment/useGetScheduledDeployment';
import GlobalRulesOverview from 'Components/GlobalRulesOverview';
import RevisionOverview from './components/RevisionOverview';
import SingleMerchantView from './components/SingleMerchantView';
import SingleTenantGroupViewScheduled from './components/SingleTenantGroupView/SingleTenantGroupViewScheduled';

import styles from './RevisionWindow.css';

const ScheduledRevisionWindow = () => {
    const { users } = useGetReduxUsers();
    const scheduledDeploymentDetails = useGetScheduledDeploymentDetails(users);
    const scheduleDeploymentOverview = useGetScheduledDeployment({ options: { polling: true } });
    const { t } = useTranslation();
    const { viewOption, setViewOption } = useRuleView();
    const user = useSelector(getUser);
    const { version } = useParams<{ version: string }>();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const [showSingleMerchant, setShowSingleMerchant] = useState(false);
    const [currMerchant, setCurrMerchant] = useState('');
    const [showSingleTenantGroup, setShowSingleTenantGroup] = useState(false);
    const [currTenantGroup, setCurrTenantGroup] = useState<TenantGroup>({});
    const showMerchants = shouldShowMerchants();
    const isGlobalRuleSelectOption = viewOption === GLOBAL_RULE_TABLE_VIEW;
    const showTenantGroups = shouldShowManageTenants(user?.landlordId, user.tenantKey) && shouldShowTenantGroups();
    const { data: landlordInfo } = useGetLandlordInfo(showTenantGroups);
    const columns = useGetTenantGroupColumns({});

    const merchantRevision = useMemo(() => {
        return scheduledDeploymentDetails.data?.merchantRevisions.find((item) => item.merchantId === currMerchant);
    }, [currMerchant, scheduledDeploymentDetails.data]);

    const globalColumns: Args['columns'] = [
        'modifiedFlag',
        'virtualRuleGroup',
        'ruleId',
        'inputDescription',
        'conclusion',
        'ruleType',
        'lastModified',
        'status'
    ];

    if (landlordInfo?.isLandlord) {
        globalColumns.push('allowOverrides');
        globalColumns.push('hiddenFromTenant');
    }
    const executionColumns: Args['columns'] = [
        'modifiedFlag',
        'order',
        'ruleId',
        'inputDescription',
        'ruleGroup',
        'conclusion',
        'ruleType'
    ];
    const revisionWindowColumns = isGlobalRuleSelectOption ? globalColumns : executionColumns;

    /** Hide columns if Feature Flags are turned off */
    const filteredRulesColumns = shouldShowAdvancedRules()
        ? revisionWindowColumns
        : revisionWindowColumns.filter((columnName) => columnName !== 'ruleType');

    const rulesColumnNames = useGetRuleColumns({
        columns: filteredRulesColumns
    });

    const merchantColumnNames = useGetMerchantOverridesColumns({
        columns: [
            'modifiedFlag',
            'merchantIdNoLink',
            'merchantName',
            'merchantDescription',
            'overallThreshold',
            'activeRules',
            'overrides',
            'lastModified',
            'status'
        ]
    });

    if (
        scheduledDeploymentDetails.isLoading ||
        scheduledDeploymentDetails.isFetching ||
        scheduleDeploymentOverview.isLoading
    ) {
        return <RouteLoading />;
    }

    const scheduledDeploymentDetailsError = errorController([scheduledDeploymentDetails.error]);
    const scheduledDeploymentOverviewError = errorController([scheduleDeploymentOverview.error]);
    if (scheduledDeploymentDetailsError.hasError || !scheduledDeploymentDetails.data)
        return <>{scheduledDeploymentDetailsError.errorCmp}</>;
    if (scheduledDeploymentOverviewError.hasError || !scheduleDeploymentOverview.data)
        return <>{scheduledDeploymentOverviewError.errorCmp}</>;

    const {
        deploymentData,
        acquirerRevision,
        merchantRevisions,
        tenantGroupRevisions
    } = scheduledDeploymentDetails.data;

    // Default or Templated Rules should not be seen in Execution Order view.
    const formatedRulesData: Rule[] = getRulesFormatForView(acquirerRevision.rules, isGlobalRuleSelectOption);

    const scheduledDeploymentData = {
        ...deploymentData,
        ...scheduleDeploymentOverview.data!,
        scheduledAt: scheduleDeploymentOverview.data!.createdAt
    };

    return (
        <div>
            <MenuBar
                id='subNavBar'
                leftAlignedMenuItems={[
                    <div className={styles.openedFromText} key='openedFrom'>
                        {t('OPENED FROM REVISIONS')}
                    </div>
                ]}
            />
            <div className={styles.pageContainer}>
                <PageHeader>
                    <PageHeader.Left className={styles.titleText}>{t('versionNum', { version })}</PageHeader.Left>
                </PageHeader>
                <RevisionOverview data={scheduledDeploymentData} scheduledOverview />
                <Tabs className={styles.revisionWindowTabs} startIndex={0} type='small'>
                    <TabContent headerTestId='rules-tab' hidden label={t('Global Rules')}>
                        <div className={styles.rulesContainer}>
                            <GlobalRulesOverview data={acquirerRevision!} readOnly />
                            <GlobalRulesTable
                                tableName='revision-window-global-rules'
                                rulesData={formatedRulesData}
                                columnNames={rulesColumnNames}
                                mode='revisionWindow'
                                groupByColumns={['virtualRuleGroup']}
                                // use revision and not deployed revision
                                deploymentVersion={Number(version)}
                                selectViewOption={viewOption}
                                setSelectViewOption={setViewOption}
                                tableInnerHeader={t('Rule Execution Order')}
                                options={{
                                    isRevision: true,
                                    isRevisionHistoryVisible: false
                                }}
                            />
                        </div>
                    </TabContent>
                    {showMerchants && (
                        <TabContent headerTestId='merchants-tab' hidden label={t('Merchants')}>
                            {showSingleMerchant ? (
                                <SingleMerchantView
                                    rulesData={merchantRevision!}
                                    merchantId={currMerchant}
                                    version={Number(version)}
                                    setShowSingleMerchant={setShowSingleMerchant}
                                    options={{
                                        fetch: false
                                    }}
                                />
                            ) : (
                                <MerchantOverridesTable
                                    rawData={(merchantRevisions! as unknown) as FlatMerchantOverrideDataItem[]}
                                    columnNames={merchantColumnNames}
                                    templateId={templateRepositoryId!}
                                    hideKebabMenu
                                    setShowSingleMerchant={setShowSingleMerchant}
                                    setCurrMerchant={setCurrMerchant}
                                    isLoading={false}
                                />
                            )}
                        </TabContent>
                    )}

                    {showTenantGroups && (
                        <TabContent headerTestId='tenantGroups-tab' hidden label={t('Tenant Groups')}>
                            {showSingleTenantGroup ? (
                                <SingleTenantGroupViewScheduled
                                    rulesData={acquirerRevision}
                                    tenantGroupRevisions={tenantGroupRevisions}
                                    currTenantGroup={currTenantGroup}
                                    setShowSingleTenantGroup={setShowSingleTenantGroup}
                                />
                            ) : (
                                <TenantGroupsTable
                                    mode='revisionWindow'
                                    deploymentData={scheduledDeploymentData!}
                                    columns={columns}
                                    setShowSingleTenantGroup={setShowSingleTenantGroup}
                                    setCurrTenantGroup={setCurrTenantGroup}
                                    options={{
                                        isScheduledDeploymentRevision: true
                                    }}
                                />
                            )}
                        </TabContent>
                    )}
                </Tabs>
            </div>
        </div>
    );
};

export default ScheduledRevisionWindow;
